import { gql, useQuery } from '@apollo/client'
import _ from 'lodash'
import moment from 'moment'
import { useMemo } from 'react'

import { useVariables } from 'pared/Routes/renderer/variables'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { IApiDataType } from '../../types'

interface IDataType {
  trendLocationPurchase: {
    nodes: {
      startDate: string
      endDate: string
      businessQuarter: number
      businessMonth: number
      businessWeek: number
      netSales: number
      itemDetails: Record<
        string,
        {
          type: 'all' | 'item' | 'category'
          item: string
          actualUsage: number
          actualUsagePercent: number
          end: number
          percent: number
        }
      >
    }[]
  }
}

const query = gql`
  query trendLocationPurchase(
    $iLocationId: Int!
    $iEndDate: Date!
    $iGroupBy: String!
  ) {
    trendLocationPurchase(
      iLocationId: $iLocationId
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
    ) {
      nodes {
        startDate
        endDate
        businessQuarter
        businessMonth
        businessWeek
        netSales
        itemDetails
      }
    }
  }
`

const formatDateString = (dateStr: string) => moment(dateStr).format('M/D/YY')

export const bbbTrendLocationPurchaseConfigs = {
  date: 'string',
  netSales: 'price',
  actualUsage: 'price',
  actualUsagePercent: 'percent',
  endInventory: 'price',
  percentOfNetSales: 'percent',
  percentOfNetSalesHeatMap: 'percent',
} as const

const useBbbTrendLocationPurchase = () => {
  const { variables } = useVariables()
  const { startDate, endDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()

  const dateType = variables.date?.value.type
  const iGroupBy = ((t) => {
    switch (t) {
      case 'year':
        return 'business_quarter'

      case 'quarter':
        return 'business_month'

      default:
        return 'business_week'
    }
  })(dateType)

  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iLocationId: groupFilter?.ids[0],
      iEndDate: endDate,
      iGroupBy,
    },
    skip: !startDate || !endDate || !groupFilter,
  })
  const category = variables.bbbTrendPurchaseCategories?.value[0]?.slice(-1)[0]
  const categoryIsLoading = !category

  return {
    data: useMemo((): IApiDataType => {
      const trendData = data?.trendLocationPurchase.nodes

      if (!trendData || !category) return null

      return trendData.map((d) => {
        const startStr = formatDateString(d.startDate)
        const endDateStr = formatDateString(d.endDate)
        const date = `
          <div style="font-family: Lexend-SemiBold; font-weight: 700; margin-top: 5px;">
            ${(() => {
              switch (dateType) {
                case 'year':
                  return `Quarter ${d.businessQuarter}`
                case 'quarter':
                  return `Period ${d.businessMonth}`
                default:
                  return `Week ${d.businessWeek}`
              }
            })()}
          </div>
          <div style="margin-bottom: 5px;">
            ${startStr} - ${endDateStr}
          </div>
        `
        const value = d.itemDetails[category]

        return {
          ...value,
          date,
          id: date as string,
          parentId: 'root',
          netSales: d.netSales,
          endInventory: value?.end,
          percentOfNetSales: value?.percent,
          percentOfNetSalesHeatmap: (() => {
            if (_.isNil(value?.percent)) return null

            if (value.percent >= 30) return 0

            if (value.percent < 30 && value.percent >= 20) return 1

            return 2
          })(),
        }
      })
    }, [data, category]),
    loading: loading || categoryIsLoading,
  }
}

export default useBbbTrendLocationPurchase
